import type { Chat } from "@storytell/cp-client";
import { subscribeToGlobalEvent } from "@storytell/events";
import type { StreamingChatId } from "@storytell/streaming";
import type { Accessor } from "solid-js";
import { batch, createEffect, createMemo, createSignal, on, onCleanup, onMount, startTransition } from "solid-js";
import { isServer } from "solid-js/web";

/** Max recent chats fetched for the sidebar. The full list lives on the /chats page. */
const SIDEBAR_RECENT_CHATS_CAP = 50;

/**
 * Chats warmed on idle so the user's likely next click renders without a spinner.
 * Kept small because every one is a full conversation fetch held in memory; the
 * rest are warmed on hover or focus of their link.
 */
const PREFETCH_TOP_N = 3;

export type RecentChatsService = ReturnType<typeof createRecentChatsService>;

interface RecentChatsServiceDeps {
  authReady: Accessor<boolean>;
  authToken: Accessor<string | undefined>;
  authUserId: Accessor<string | undefined>;
  projectId: Accessor<string | undefined>;
  /** The most recent chats in the URL's project; resolves to an empty list when the request fails. */
  listRecentChats: (pageSize: number, token: string | undefined) => Promise<ChatSummary[]>;
  /** Warm the chat-conversation cache for the given chats (no-op on the server). */
  prefetchChats: (chatIds: StreamingChatId[]) => void;
}

type ChatSummary = Chat.ChatSummary;

/**
 * Fetches up to SIDEBAR_RECENT_CHATS_CAP most recent chats for the sidebar.
 * Single-shot fetch — the /chats page owns the full, searchable list.
 */
export function createRecentChatsService(deps: RecentChatsServiceDeps) {
  const [chats, setChats] = createSignal<ChatSummary[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);

  // Bumped on every reset so an in-flight fetch can detect it's been superseded
  // (by reset/refetch triggered from a mutation event) and drop its stale result
  // instead of writing it over freshly-loaded state.
  let requestGen = 0;

  /** True when the server returned the full cap — indicates more chats exist on /chats. */
  const hitCap = createMemo(() => chats().length >= SIDEBAR_RECENT_CHATS_CAP);

  async function load() {
    if (isLoading()) return;
    const myGen = ++requestGen;
    setIsLoading(true);
    let fetched: ChatSummary[] = [];
    try {
      fetched = await deps.listRecentChats(SIDEBAR_RECENT_CHATS_CAP, deps.authToken());
    } catch (err) {
      // A thrown error must not leave isLoading stuck true; the `if (isLoading()) return` guard
      // would then permanently block every subsequent load. Fall through with an empty list.
      console.error("Failed to load recent chats", err);
    }
    if (myGen !== requestGen) return;
    batch(() => {
      setChats(fetched);
      setIsLoading(false);
    });
  }

  function reset() {
    requestGen++;
    batch(() => {
      setChats([]);
      setIsLoading(false);
    });
  }

  // Kick off the fetch when auth is ready AND the URL has settled on a project —
  // listRecentChats requires X-Project-Id, so gate on projectId too. Tracking it
  // also makes the list reload when the user switches projects.
  createEffect(
    on(
      () => {
        if (isServer || !deps.authReady() || !deps.authUserId()) return undefined;
        return deps.projectId();
      },
      (projectId) => {
        if (!projectId) {
          // Project-less routes (e.g. /settings) carry no X-Project-Id, so the URL
          // projectId drops to undefined. Keep the last project's recents on screen
          // rather than blanking the sidebar; only clear once auth itself is gone.
          if (!deps.authReady() || !deps.authUserId()) reset();
          return;
        }
        void startTransition(() => {
          reset();
          void load();
        });
      }
    )
  );

  // Warm the top chats once the list arrives, on browser idle so warming never
  // competes with the first paint. requestIdleCallback is unavailable in some
  // browsers (and in Safari historically), so fall back to a short timeout.
  createEffect(
    on(chats, (list) => {
      if (isServer || list.length === 0) return;
      const ids = list.slice(0, PREFETCH_TOP_N).map((c) => c.chatId as StreamingChatId);
      const warm = () => deps.prefetchChats(ids);
      if (typeof requestIdleCallback === "function") {
        const handle = requestIdleCallback(warm);
        onCleanup(() => cancelIdleCallback(handle));
      } else {
        const handle = setTimeout(warm, 200);
        onCleanup(() => clearTimeout(handle));
      }
    })
  );

  const refetch = () => {
    if (!deps.projectId()) return;
    void startTransition(() => {
      reset();
      void load();
    });
  };

  const refetchEvents = [
    "new-chat-created",
    "user-context-changed",
    "chat-title-generated",
    "chat-title-updated",
    "chat-deleted",
    "chat-moved",
  ] as const;

  // The event target these ride on is module-scoped, so a subscription outlives
  // the service unless it is released with it: on the server that is the whole
  // process, where every rendered page would leave another set behind. onMount
  // keeps them off the server render and pairs each one with its release.
  onMount(() => {
    const unsubscribes = refetchEvents.map((e) => subscribeToGlobalEvent(e, refetch));

    // The event carries the new value, so patch the summary instead of refetching.
    unsubscribes.push(
      subscribeToGlobalEvent("chat-visibility-updated", (event) => {
        const { chatId, visibility } = event.detail;
        setChats((prev) => prev.map((c) => (c.chatId === chatId ? { ...c, visibility } : c)));
      })
    );

    onCleanup(() => {
      for (const unsubscribe of unsubscribes) unsubscribe();
    });
  });

  return {
    /** Up to SIDEBAR_RECENT_CHATS_CAP most recent chats. */
    chats,
    /** True when the list is full — signals more chats exist on /chats. */
    hitCap,
  };
}
